"use client";

import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowRight,
  Bell,
  Building2,
  Check,
  ChevronDown,
  Gauge,
  Home,
  Mail,
  MapPin,
  Star,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/Button";
import { Panel, PanelGrid } from "@/components/ui/Panel";
import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { PublicHeroChecklist, PublicHeroCopy } from "@/components/PublicHero";
import LazyDemo from "@/components/index/sections/LazyDemo";
import { CombinedPortfolioSection } from "@/components/index/sections/hero/CombinedPortfolioSection";
import { WatchDogSelectionPanelCard } from "@/components/hlidaci-pes/watchdog-page/WatchDogSelectionPanelCard";
import { EstateOfferImage } from "@/components/hlidaci-pes/EstateOfferImage";
import { HomeLandingContent } from "@/components/index/HomeLandingContent";
import { ListingCarousel } from "@/components/index/fragments/ListingCarousel";
import Membership from "@/components/index/fragments/Memberhip";
import { RankedEstateIntroSlide } from "@/components/index/fragments/RankedEstateIntroSlide";
import { RankedEstateMembershipSlide } from "@/components/index/fragments/RankedEstateMembershipSlide";
import { RankedEstateSlide } from "@/components/index/fragments/RankedEstateSlide";
import {
  getSiteContent,
  getWatchdogPricingFeatureRows,
  type PricingFeatureRow,
} from "@/content/site";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { getLatestBlogPosts } from "@/lib/blog";
import { HOME_FAQ } from "@/lib/seo";
import {
  consumePendingAuthMode,
  storePendingAuthMode,
} from "@/lib/auth-navigation";
import { formatCzkPrice } from "@/lib/price";
import type {
  EstateOffer,
  PublicReview,
  PublicReviewPayload,
  PublicWatchDogOffer,
  PublicWatchDogOfferGroup,
  WatchDog,
} from "@/types";
import {
  dismissToastLater,
  fetchPublicMostExpensiveWeeklyOffers,
  fetchPublicReviews,
  showLoadingToast,
  submitPublicReview,
} from "@/utils";

type ReviewFormState = PublicReviewPayload;

const emptyReviewForm: ReviewFormState = {
  name: "",
  stars: 0,
  review: "",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeReview(value: unknown, index: number): PublicReview | null {
  if (!isRecord(value)) return null;

  const name = typeof value.name === "string" ? value.name.trim() : "";
  const review =
    typeof value.review === "string"
      ? value.review.trim()
      : typeof value.message === "string"
        ? value.message.trim()
        : "";
  const stars = Number(value.stars ?? value.rating ?? 5);

  if (!name || !review) return null;

  return {
    id: typeof value.id === "number" ? value.id : index,
    name,
    review,
    stars: Number.isFinite(stars)
      ? Math.min(Math.max(Math.round(stars), 0), 5)
      : 5,
    createdAt:
      typeof value.createdAt === "string" ? value.createdAt : undefined,
    createdAtFormatted:
      typeof value.createdAtFormatted === "string"
        ? value.createdAtFormatted
        : undefined,
  };
}

function normalizeReviews(data: unknown): PublicReview[] {
  let candidate: unknown[] = [];

  if (Array.isArray(data)) {
    candidate = data;
  } else if (isRecord(data) && Array.isArray(data.reviews)) {
    candidate = data.reviews;
  } else if (
    isRecord(data) &&
    isRecord(data.data) &&
    Array.isArray(data.data.reviews)
  ) {
    candidate = data.data.reviews;
  } else if (isRecord(data) && Array.isArray(data.items)) {
    candidate = data.items;
  } else if (isRecord(data) && Array.isArray(data.data)) {
    candidate = data.data;
  }

  return candidate
    .map((item, index) => normalizeReview(item, index))
    .filter((item): item is PublicReview => item !== null);
}

function normalizeWeeklyOfferGroups(data: unknown): PublicWatchDogOfferGroup[] {
  const groups =
    isRecord(data) && Array.isArray(data.data)
      ? data.data
      : Array.isArray(data)
        ? data
        : [];

  return groups.flatMap((group) => {
    if (!isRecord(group) || !Array.isArray(group.offers)) {
      return [];
    }

    const offers = group.offers.filter(
      (offer): offer is PublicWatchDogOffer => {
        return (
          isRecord(offer) &&
          typeof offer.id === "number" &&
          typeof offer.price === "string"
        );
      },
    );

    return [
      {
        watchdogId: typeof group.watchdogId === "number" ? group.watchdogId : 0,
        estateType:
          typeof group.estateType === "string"
            ? group.estateType
            : "Nemovitosti",
        offerType:
          typeof group.offerType === "string" ? group.offerType : "Prodej",
        searchAddress:
          typeof group.searchAddress === "string"
            ? group.searchAddress
            : "Česko",
        offers,
        favoriteOffers: Array.isArray(group.favoriteOffers)
          ? group.favoriteOffers.filter(
              (offer): offer is PublicWatchDogOffer =>
                isRecord(offer) &&
                typeof offer.id === "number" &&
                typeof offer.price === "string",
            )
          : [],
      },
    ];
  });
}

function getCity(offer: PublicWatchDogOffer, fallback: string) {
  const location = offer.address || fallback;
  return location.replace(/\d+/g, "").trim() || fallback;
}

function selectWeeklyOfferGroups(groups: PublicWatchDogOfferGroup[]) {
  const flatGroup = groups.find((group) => group.estateType === "Byty");
  const houseGroup = groups.find((group) => group.estateType === "Domy");

  return [flatGroup, houseGroup].filter(
    (group): group is PublicWatchDogOfferGroup => Boolean(group),
  );
}

function SectionHeading({
  eyebrow,
  title,
  text,
  align = "center",
}: {
  eyebrow: string;
  title: string;
  text?: string;
  align?: "center" | "left";
}) {
  return (
    <div
      className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : "text-left"}`}
    >
      <div
        className={`mb-5 flex items-center gap-3 ${align === "center" ? "justify-center" : "justify-start"}`}
      >
        <span className="h-px w-9 bg-softiq-primary/45" aria-hidden />
        <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-softiq-primary/70">
          {eyebrow}
        </p>
      </div>
      <h2 className="text-3xl !font-medium leading-[1.08] tracking-[-0.035em] text-softiq-copy sm:text-5xl">
        {title}
      </h2>
      {text ? (
        <p className="mt-4 text-base leading-7 text-softiq-copy-muted sm:text-lg">
          {text}
        </p>
      ) : null}
    </div>
  );
}

function Stars({
  value,
  onChange,
}: {
  value: number;
  onChange?: (value: number) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => {
        const active = star <= value;
        const icon = (
          <Star
            className={`size-5 ${
              active
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200"
            }`}
            strokeWidth={0}
            aria-hidden
          />
        );

        if (!onChange) return <span key={star}>{icon}</span>;

        return (
          <Button
            key={star}
            type="button"
            variant="ghost"
            size="icon"
            className="hover:scale-110"
            aria-label={`${star}/5`}
            onClick={() => onChange(star)}
          >
            {icon}
          </Button>
        );
      })}
    </div>
  );
}

function PublicMostExpensiveOfferCard({
  offer,
}: {
  offer: PublicWatchDogOffer;
}) {
  return (
    <div className="relative size-full overflow-hidden bg-softiq-secondary">
      <EstateOfferImage
        source={offer.mainPicUrl}
        origin={offer.origin}
        offerUrl={offer.offerUrl}
        alt={offer.header || offer.address || "Ukázková realitní nabídka"}
        sizes="(min-width: 1024px) 300px, 100vw"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/35" />
      <div className="absolute inset-x-4 bottom-0 rounded-t-2xl bg-white/85 px-5 py-3 text-softiq-primary backdrop-blur-md">
        <p className="truncate text-base font-medium">
          {formatCzkPrice(offer.price)}
        </p>
      </div>
    </div>
  );
}

function createPublicWatchDog(
  group: PublicWatchDogOfferGroup,
  id: number,
): WatchDog {
  return {
    id,
    doResetSearch: false,
    isNew: false,
    client: null,
    isFirstSyncCompleted: true,
    isEnabled: true,
    doEmailReport: true,
    emailReportFrequency: "DAILY",
    emailReportSetting: "",
    sendOfferAs: "NEW",
    realityUrl: [],
    estateType: group.estateType,
    offerType: group.offerType || "Prodej",
    flatCategory: [],
    homeCategory: [],
    landCategory: [],
    comercialCategory: [],
    otherCategory: [],
    exactAddress: "",
    searchAddress: group.searchAddress || "Česko",
    inRange: "",
    estateCondition: [],
    floorFrom: null,
    floorTo: null,
    usableAreaFrom: null,
    usableAreaTo: null,
    priceFrom: null,
    priceTo: null,
    urlSreality: "",
    urlBazos: "",
    urlBezrealitky: "",
    urlIdnesReality: "",
  };
}

function createPublicEstateOffer(
  offer: PublicWatchDogOffer,
  watchDog: WatchDog,
): EstateOffer {
  return {
    address: offer.address || "",
    createdAt: offer.createdAt,
    header: offer.header || "",
    id: offer.id,
    isReported: false,
    isFavorite: false,
    isUpdated: false,
    mainPicUrl: offer.mainPicUrl || "",
    offerId: String(offer.id),
    offerUrl: offer.offerUrl || "#",
    origin: offer.origin || "",
    price: offer.price,
    squareMeter: offer.squareMeter || "",
    pricePerSquareMeter: offer.pricePerSquareMeter || "",
    softiqIndex: Number(offer.softiqIndex) || null,
    comparisonText: offer.comparisonText || "",
    watchdogEntity: {
      id: watchDog.id,
      client: {},
      isFirstSyncCompleted: true,
      isEnabled: true,
      doSendUpdated: false,
      searchAddress: watchDog.searchAddress,
      offerType: watchDog.offerType,
      estateType: watchDog.estateType,
    },
  };
}

const publicDemoWatchDogs = [
  {
    watchdogId: -1001,
    searchAddress: "Česko",
    estateType: "Domy",
    offerType: "Prodej",
    priceChange: 1.2,
    resultCount: 26,
    prices: ["25 000 000 Kč", "21 900 000 Kč", "18 750 000 Kč"],
  },
  {
    watchdogId: -1002,
    searchAddress: "Olomouc",
    estateType: "Byty",
    offerType: "Prodej",
    priceChange: 1.5,
    resultCount: 23,
    prices: ["8 490 000 Kč", "7 950 000 Kč", "7 290 000 Kč"],
  },
  {
    watchdogId: -1003,
    searchAddress: "Ostrava",
    estateType: "Byty",
    offerType: "Prodej",
    priceChange: 1.9,
    resultCount: 19,
    prices: ["6 890 000 Kč", "5 990 000 Kč", "5 450 000 Kč"],
  },
] as const;

export function WatchDogPreview() {
  const { language } = useLanguage();
  const [groups, setGroups] = useState<PublicWatchDogOfferGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWatchDogId, setSelectedWatchDogId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    let isMounted = true;

    async function loadOffers() {
      try {
        const response = await fetchPublicMostExpensiveWeeklyOffers();

        if (isMounted && response.status >= 200 && response.status < 300) {
          setGroups(
            normalizeWeeklyOfferGroups(response.data)
              .filter((group) => group.offers.length > 0)
              .slice(0, 3),
          );
        }
      } catch (error) {
        console.error("Failed to load public watchdog banner:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadOffers();

    return () => {
      isMounted = false;
    };
  }, []);

  const sourceOffers = useMemo(
    () => groups.flatMap((group) => group.offers),
    [groups],
  );
  const bannerData = useMemo(
    () =>
      publicDemoWatchDogs.map((definition, watchDogIndex) => {
        const group: PublicWatchDogOfferGroup = {
          watchdogId: definition.watchdogId,
          searchAddress: definition.searchAddress,
          estateType: definition.estateType,
          offerType: definition.offerType,
          offers: [],
          favoriteOffers: [],
        };
        const watchDog = createPublicWatchDog(group, definition.watchdogId);
        const publicOffers = definition.prices.map((price, offerIndex) => {
          const sourceOffer = sourceOffers.length
            ? sourceOffers[
                (watchDogIndex * definition.prices.length + offerIndex) %
                  sourceOffers.length
              ]
            : null;
          const offer: PublicWatchDogOffer = {
            id: definition.watchdogId * 100 - offerIndex,
            createdAt: new Date(
              Date.now() - offerIndex * 4 * 24 * 60 * 60 * 1000,
            ).toISOString(),
            header: `${definition.estateType} na prodej · ${definition.searchAddress}`,
            address: definition.searchAddress,
            price,
            squareMeter: sourceOffer?.squareMeter ?? null,
            pricePerSquareMeter: sourceOffer?.pricePerSquareMeter ?? null,
            softiqIndex: sourceOffer?.softiqIndex ?? null,
            comparisonText: sourceOffer?.comparisonText ?? null,
            mainPicUrl: sourceOffer?.mainPicUrl ?? null,
            origin: sourceOffer?.origin ?? null,
            offerUrl: sourceOffer?.offerUrl ?? null,
          };

          return offer;
        });

        return {
          watchDog,
          publicOffers,
          offers: publicOffers.map((offer) =>
            createPublicEstateOffer(offer, watchDog),
          ),
        };
      }),
    [sourceOffers],
  );
  const publicWatchDogs = bannerData.map((item) => item.watchDog);
  const publicEstateOffers = bannerData.flatMap((item) => item.offers);
  const mostExpensiveOffers = bannerData
    .flatMap((item) => item.publicOffers)
    .sort((a, b) => {
      const priceA = Number(a.price.replace(/[^\d]/g, ""));
      const priceB = Number(b.price.replace(/[^\d]/g, ""));
      return priceB - priceA;
    })
    .slice(0, 5);
  const openRegistration = () => {
    storePendingAuthMode("register");
    window.location.assign("/#prihlaseni");
  };
  const watchDogCountLabel =
    language === "cs"
      ? publicWatchDogs.length === 1
        ? "1 hlídací pes"
        : publicWatchDogs.length > 1 && publicWatchDogs.length < 5
          ? `${publicWatchDogs.length} hlídací psi`
          : `${publicWatchDogs.length} hlídacích psů`
      : publicWatchDogs.length === 1
        ? "1 watchdog"
        : `${publicWatchDogs.length} watchdogs`;
  const displayedPriceChange =
    publicDemoWatchDogs.find(
      (watchDog) => watchDog.watchdogId === selectedWatchDogId,
    )?.priceChange ?? 1.5;
  const displayedResultCount =
    publicDemoWatchDogs.find(
      (watchDog) => watchDog.watchdogId === selectedWatchDogId,
    )?.resultCount ?? 68;

  return (
    <div className="relative mx-auto w-full max-w-[680px]">
      <div className="relative border-l border-white/20 pl-4 sm:pl-6">
        <WatchDogSelectionPanelCard
          watchdogs={publicWatchDogs}
          watchDogCountLabel={watchDogCountLabel}
          isLoading={isLoading}
          selectedWatchDogId={selectedWatchDogId}
          onSelectWatchDog={(watchDog) =>
            setSelectedWatchDogId((current) =>
              current === watchDog.id ? null : watchDog.id,
            )
          }
          tooltip={
            language === "cs" ? "Přidat hlídacího psa" : "Add a watchdog"
          }
          isMembershipLoading={false}
          onAddWatchDogClick={openRegistration}
          latestOffers={publicEstateOffers}
          summaryOffers={publicEstateOffers}
          isSummaryLoading={isLoading}
          summaryResultCountOverride={displayedResultCount}
          latestResultLabelOverride={language === "cs" ? "Dnes" : "Today"}
          averagePriceChangeOverride={displayedPriceChange}
          isDemo
          latestContent={
            <div className="relative h-[240px] min-w-0 max-w-full overflow-hidden [&_.demo-carousel-wrap]:h-full [&_.demo-carousel-wrap]:min-w-0 [&_.demo-carousel-wrap]:max-w-full [&_.demo-carousel-wrap]:overflow-hidden [&_.demo-slide]:!h-[240px] [&_.demo-slide]:!rounded-none [&_.demo-slide>div]:!rounded-none [&_.demo-swiper]:!h-full [&_.demo-swiper]:!w-full [&_.demo-swiper]:!max-w-full [&_.demo-swiper]:!rounded-none [&_.listing-next-public-watchdogs]:!right-3 [&_.listing-prev-public-watchdogs]:!left-3 [&_.swiper]:!pb-0">
              <span className="app-pill-overlay">Nejdražší nabídky týdne</span>
              <ListingCarousel
                offers={mostExpensiveOffers}
                navigationId="public-watchdogs"
                previousLabel="Předchozí nabídka"
                nextLabel="Další nabídka"
                renderOffer={(offer) => (
                  <PublicMostExpensiveOfferCard offer={offer} />
                )}
                emptyContent={
                  <div className="flex h-[240px] items-center justify-center px-6 text-center text-sm text-softiq-primary/50 dark:text-white/50">
                    Veřejné výsledky se teď nepodařilo načíst.
                  </div>
                }
                showPagination={false}
                loop={false}
                autoPlay={false}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}

export function AuthPanel() {
  const { handleSubmit, doForgottenPassword, setDoForgottenPassword } =
    useSessionContext();
  const { doLogin, setDoLogin, errorMessage, isLoading } = useAppContext();

  useEffect(() => {
    const authMode = consumePendingAuthMode();

    if (authMode === "register") {
      setDoLogin(false);
      setDoForgottenPassword(false);
    } else if (authMode === "login") {
      setDoLogin(true);
      setDoForgottenPassword(false);
    }
  }, [setDoForgottenPassword, setDoLogin]);

  const title = doForgottenPassword
    ? "Obnova hesla"
    : doLogin
      ? "Přihlášení"
      : "Registrace";

  return (
    <Panel
      as="section"
      id="prihlaseni"
      variant="shell"
      className="mx-auto w-full max-w-md p-5 sm:p-7 lg:ml-auto lg:mr-0"
    >
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="app-block-eyebrow">14 dní zdarma</p>
          <h2 className="app-block-title mt-2">{title}</h2>
        </div>
        <div className="rounded-full border border-softiq-primary bg-softiq-primary p-3 text-white">
          <Home className="size-5" aria-hidden />
        </div>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <label className="app-form-field">
          Email
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
            placeholder="vas@email.cz"
          />
        </label>

        {!doForgottenPassword ? (
          <label className="app-form-field">
            Heslo
            <input
              name="password"
              type="password"
              autoComplete={doLogin ? "current-password" : "new-password"}
              required
              className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
              placeholder="••••••••"
            />
          </label>
        ) : null}

        {!doLogin && !doForgottenPassword ? (
          <label className="app-form-field">
            Heslo znovu
            <input
              name="password-repeat"
              type="password"
              autoComplete="new-password"
              required
              className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
              placeholder="••••••••"
            />
          </label>
        ) : null}

        {errorMessage ? <Alert tone="danger">{errorMessage}</Alert> : null}

        <Button
          type="submit"
          variant="brand"
          size="form"
          loading={isLoading}
          loadingLabel="Načítání..."
          className="w-full"
        >
          {doForgottenPassword
            ? "Obnovit heslo"
            : doLogin
              ? "Přihlásit se"
              : "Vytvořit účet"}
        </Button>
      </form>

      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          size="form"
          onClick={() => setDoForgottenPassword(true)}
        >
          Zapomenuté heslo
        </Button>
        <Button
          type="button"
          variant="outline"
          size="form"
          onClick={() => {
            setDoLogin((current) => !current);
            setDoForgottenPassword(false);
          }}
        >
          {doLogin ? "Registrace" : "Přihlášení"}
        </Button>
      </div>
    </Panel>
  );
}

function ReviewsPanel({ portfolioFirst = false }: { portfolioFirst?: boolean }) {
  const [reviews, setReviews] = useState<PublicReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<ReviewFormState>(emptyReviewForm);

  const loadReviews = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetchPublicReviews();

      if (response.status >= 200 && response.status < 300) {
        setReviews(normalizeReviews(response.data).slice(0, 6));
      }
    } catch (error) {
      console.error("Failed to load public reviews:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReviews();
  }, [loadReviews]);

  async function handleReviewSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const payload = {
      name: form.name.trim(),
      stars: form.stars,
      review: form.review.trim(),
    };

    if (!payload.name || !payload.review) {
      toast.error("Vyplňte prosím jméno i recenzi.");
      return;
    }

    const toastId = showLoadingToast("Odesílám...");
    setIsSubmitting(true);

    try {
      const response = await submitPublicReview(payload);

      if (response.status >= 200 && response.status < 300) {
        toast.success("Děkujeme, recenze byla odeslána ke schválení.");
        setForm(emptyReviewForm);
        void loadReviews();
      } else {
        toast.error("Recenzi se nepodařilo odeslat.");
      }
    } catch (error) {
      console.error("Failed to submit public review:", error);
      toast.error("Recenzi se nepodařilo odeslat.");
    } finally {
      setIsSubmitting(false);
      dismissToastLater(toastId);
    }
  }

  return (
    <section
      id="recenze"
      className="section-warm scroll-mt-4 bg-softiq-surface-muted px-5 py-20 sm:scroll-mt-6 sm:px-8"
    >
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <SectionHeading
            align="left"
            eyebrow="Recenze"
            title="Co říkají uživatelé"
            text={
              portfolioFirst
                ? "SOFTIQ pomáhá majitelům udržet správu realitního portfolia, finance a důležité termíny v jednom srozumitelném přehledu."
                : "Realitní příležitosti mizí rychle. SOFTIQ pomáhá lidem sledovat trh bez každodenního ručního procházení inzerce."
            }
          />

          <Panel
            as="form"
            variant="shell"
            className="mt-8 p-5"
            onSubmit={handleReviewSubmit}
          >
            <div className="grid gap-4">
              <label className="app-form-field">
                Jméno
                <input
                  value={form.name}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  className="h-11 rounded-lg border border-softiq-border bg-softiq-surface px-4 outline-none focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
                  placeholder="Vaše jméno"
                />
              </label>

              <div className="app-form-field">
                Vyberte hodnocení
                <Stars
                  value={form.stars}
                  onChange={(stars) =>
                    setForm((current) => ({ ...current, stars }))
                  }
                />
              </div>

              <label className="app-form-field">
                Recenze
                <textarea
                  value={form.review}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      review: event.target.value,
                    }))
                  }
                  className="min-h-28 rounded-lg border border-softiq-border bg-softiq-surface px-4 py-3 outline-none focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15"
                  placeholder="Jaká je vaše zkušenost?"
                />
              </label>
            </div>

            <Button
              type="submit"
              variant="brand"
              size="compact"
              loading={isSubmitting}
              loadingLabel="Odesílám..."
              className="mt-5"
            >
              Přidat recenzi
            </Button>
          </Panel>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {isLoading
            ? [1, 2, 3, 4].map((item) => (
                <Skeleton key={item} className="h-48" />
              ))
            : reviews.length > 0
              ? reviews.map((review) => (
                  <Panel
                    as="article"
                    key={`${review.id}-${review.name}`}
                    variant="shell"
                    className="p-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-softiq-copy">
                        {review.name}
                      </h3>
                      <Stars value={review.stars} />
                    </div>
                    <p className="mt-4 leading-7 text-softiq-copy-muted">
                      “{review.review}”
                    </p>
                  </Panel>
                ))
              : [1, 2, 3, 4].map((item) => (
                  <Skeleton
                    key={`empty-review-skeleton-${item}`}
                    className="h-48"
                    aria-hidden
                  />
                ))}
        </div>
      </div>
    </section>
  );
}

function WeeklyExpensiveOffers() {
  const [groups, setGroups] = useState<PublicWatchDogOfferGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadOffers() {
      setIsLoading(true);

      try {
        const response = await fetchPublicMostExpensiveWeeklyOffers();

        if (isMounted && response.status >= 200 && response.status < 300) {
          setGroups(
            selectWeeklyOfferGroups(normalizeWeeklyOfferGroups(response.data)),
          );
        }
      } catch (error) {
        console.error("Failed to load weekly expensive offers:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadOffers();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isLoading || window.location.hash !== "#recenze") {
      return;
    }

    // The loading placeholders are taller than the rendered offers. Re-align
    // the initial hash after React has committed the final offers layout.
    let secondFrame: number | undefined;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        document.getElementById("recenze")?.scrollIntoView({ block: "start" });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) {
        window.cancelAnimationFrame(secondFrame);
      }
    };
  }, [isLoading]);

  return (
    <section className="section-warm bg-softiq-surface-muted px-5 py-20 sm:px-8">
      <div className="mx-auto w-full">
        <SectionHeading align="left" eyebrow="Ukázka výsledků" title="" />

        <div className="mt-10 gap-y-8 lg:gap-x-14 xl:gap-x-20 2xl:gap-x-28 mx-auto max-w-7xl">
          {isLoading ? (
            [1, 2].map((item) => <Skeleton key={item} className="h-[500px]" />)
          ) : groups.length > 0 ? (
            groups.map((group, groupIndex) => {
              const offers = group.offers.slice(0, 3).reverse();

              return (
                <article key={`${group.watchdogId}-${group.estateType}`}>
                  <ListingCarousel
                    offers={offers}
                    autoPlay={false}
                    navigationId={`new-weekly-${group.estateType}-${group.watchdogId}`}
                    previousLabel={`Předchozí: ${group.estateType}`}
                    nextLabel={`Další: ${group.estateType}`}
                    leadingSlide={
                      offers.length > 0 ? (
                        <RankedEstateIntroSlide
                          offer={offers[offers.length - 1]}
                          estateType={group.estateType}
                          questionIndex={groupIndex}
                        />
                      ) : undefined
                    }
                    trailingSlide={
                      offers.length > 0 ? (
                        <RankedEstateMembershipSlide
                          offer={offers[offers.length - 1]}
                        />
                      ) : undefined
                    }
                    renderOffer={(offer, index) => (
                      <RankedEstateSlide
                        offer={offer}
                        rank={offers.length - index}
                        city={getCity(offer, "Česko")}
                      />
                    )}
                    emptyContent={
                      <Panel
                        variant="shell"
                        className="flex min-h-80 items-center justify-center p-6 text-center text-softiq-copy-muted"
                      >
                        Pro tuto kategorii se teď nepodařilo načíst ukázkový
                        inzerát.
                      </Panel>
                    }
                  />
                </article>
              );
            })
          ) : (
            <Panel
              variant="shell"
              className="p-6 text-softiq-copy-muted lg:col-span-2"
            >
              Ukázkové inzeráty se teď nepodařilo načíst. Přehled se na produkci
              doplní z veřejných výsledků hlídacího psa.
            </Panel>
          )}
        </div>
      </div>
    </section>
  );
}

function DemoSection({ portfolioFirst = false }: { portfolioFirst?: boolean }) {
  return (
    <section id="ukazka" className="section-light bg-white px-5 py-20 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          eyebrow="Ukázka"
          title={
            portfolioFirst
              ? "Podívejte se na správu realitního portfolia"
              : "Podívejte se, jak to funguje"
          }
          text={
            portfolioFirst
              ? "Projděte si přehled portfolia, finance nemovitostí a reporty, které vám pomáhají při každodenní správě portfolia."
              : "Přehledné reporty přímo v mailu nebo na webu - vždy víte, co se děje."
          }
        />
        <div className="mt-10">
          <LazyDemo />
        </div>
      </div>
    </section>
  );
}

function BlogPreview({ portfolioFirst = false }: { portfolioFirst?: boolean }) {
  const { blog } = useAppContext();
  const posts = getLatestBlogPosts(blog, 3);

  return (
    <section
      id="pruvodce"
      className="section-light bg-white px-5 py-20 sm:px-8"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            align="left"
            eyebrow="Průvodce"
            title={
              portfolioFirst
                ? "Průvodce správou realitního portfolia"
                : "Články pro lepší rozhodování na realitním trhu"
            }
            text={
              portfolioFirst
                ? "Praktické články o správě nemovitostí, financích, výkonnosti portfolia, pronájmu i hledání dalších investičních příležitostí."
                : "Tipy a souvislosti k nákupu, sledování trhu a vyhodnocování nabídek. Praktický doplněk k automatickým notifikacím."
            }
          />
          <Button asChild variant="outline" size="compact" className="w-fit">
            <Link href="/pruvodce">
              Všechny články
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          </Button>
        </div>

        {posts.length > 0 ? (
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {posts.map((post) => (
              <Panel
                as={Link}
                key={post.id}
                href={`/pruvodce/${post.url}`}
                variant="shell"
                className="group overflow-hidden p-0 transition hover:-translate-y-0.5 hover:shadow-md dark:hover:border-softiq-border-dark-hover"
              >
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-softiq-copy-muted dark:text-softiq-copy-inverse-muted">
                    Realitní průvodce
                  </p>
                  <h3 className="mt-3 line-clamp-2 text-xl font-semibold leading-7 text-softiq-copy dark:text-softiq-copy-inverse">
                    {post.title}
                  </h3>
                  <p className="mt-3 line-clamp-4 text-sm leading-7 text-softiq-copy-muted dark:text-softiq-copy-inverse-muted">
                    {(post.summary || post.content_html || "")
                      .replace(/<[^>]*>/g, "")
                      .slice(0, 260)
                      .trim()}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-softiq-copy transition-colors group-hover:text-softiq-copy-muted dark:text-softiq-copy-inverse dark:group-hover:text-softiq-copy-inverse-muted">
                    Číst článek
                    <ArrowRight className="size-4" aria-hidden />
                  </span>
                </div>
              </Panel>
            ))}
          </div>
        ) : (
          <div className="mt-10 grid gap-5 lg:grid-cols-3" aria-hidden>
            {[1, 2, 3].map((item) => (
              <Skeleton key={item} className="h-80" />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section
      id="caste-dotazy"
      className="section-warm bg-softiq-surface-muted px-5 py-20 sm:px-8"
    >
      <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
        <SectionHeading
          align="left"
          eyebrow="Časté dotazy"
          title="Co vědět o správě nemovitostí v SOFTIQ"
          text="Krátké odpovědi k evidenci portfolia, financím a hledání dalších realitních příležitostí."
        />

        <div className="space-y-3">
          {HOME_FAQ.map((item) => (
            <details
              key={item.question}
              className="group rounded-2xl border-b border-softiq-border py-5 first:border-t"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-lg font-semibold text-softiq-copy">
                {item.question}
                <span className="flex size-8 flex-none items-center justify-center rounded-full border border-softiq-border bg-white dark:border-white/15 dark:bg-white/10">
                  <ChevronDown
                    className="size-4 text-softiq-primary transition-transform duration-200 group-open:rotate-180 dark:text-softiq-secondary"
                    aria-hidden
                  />
                </span>
              </summary>
              <p className="mt-4 max-w-3xl leading-7 text-softiq-copy-muted">
                {item.answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingFeatureList({
  features,
  featured,
}: {
  features: readonly PricingFeatureRow[];
  featured: boolean;
}) {
  return (
    <ul className="mt-3 flex flex-col gap-3">
      {features.map((feature) => (
        <li
          key={feature.label}
          aria-hidden={!feature.included}
          className={`grid min-h-6 grid-cols-[1rem_minmax(0,1fr)] items-start gap-x-2 text-left text-sm leading-6 ${
            feature.included ? "" : "invisible"
          }`}
        >
          <Check
            className={`mt-0.5 size-4 flex-none ${
              featured
                ? "text-softiq-secondary-shade-200 dark:text-softiq-secondary"
                : "text-softiq-secondary-shade-700 dark:text-softiq-secondary"
            }`}
            aria-hidden
          />
          <span
            className={`min-w-0 ${
              featured ? "text-white/82" : "text-softiq-copy-muted"
            }`}
          >
            {feature.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function NewHomePage({
  heroVariant = "auth",
  pageVariant = "watchdog",
}: {
  heroVariant?: "auth" | "watchdog";
  pageVariant?: "watchdog" | "combined";
}) {
  const { language } = useLanguage();
  const { pricingPlans } = getSiteContent(language);
  const { isLoggedIn, setDoLogin } = useAppContext();
  const { isPendingRegistration, selectedProduct, setSelectedProduct } =
    useSessionContext();
  const authenticationHref =
    heroVariant === "watchdog" ? "/#prihlaseni" : "#prihlaseni";

  const copy = useMemo(() => {
    if (pageVariant === "combined") {
      return language === "cs"
        ? {
            audience: "Nástroj pro majitele nemovitostí a realitní investory",
            heroLead: "Správa realitního portfolia",
            heroAccent: "Hodnota, finance, dokumenty a provoz pod kontrolou.",
            heroText:
              "Spravujte vlastní realitní portfolio a finance svých nemovitostí v jednom přehledu. Správu portfolia doplňuje hlídací pes pro hledání dalších příležitostí.",
            primaryAction: "Začít zdarma",
            secondaryAction: "Prohlédnout funkce",
            heroStats: [
              ["Portfolio", "všechny nemovitosti pohromadě"],
              ["Finance", "příjmy a výdaje v přehledu"],
              ["Vše v jednom", "pro majitele a investory"],
            ],
            watchdogEyebrow: "Správa portfolia",
            watchdogTitle: "Portfolio není jen seznam nemovitostí.",
            watchdogText:
              "SOFTIQ propojuje správu nemovitostí, finance, dokumenty, termíny a vývoj hodnoty. Získáte ucelený přehled o každé nemovitosti i celém realitním portfoliu.",
            howEyebrow: "Hlídací pes",
            howTitle: "Jeden filtr, více zdrojů, rychlejší reakce.",
            howText:
              "Hlídací pes doplňuje správu portfolia o průběžné hledání další vhodné nemovitosti.",
            stepLabel: "Krok",
            painPoints: [
              {
                title: "Data portfolia jsou roztříštěná",
                text: "Hodnota nemovitostí, dokumenty a provozní informace často končí v různých tabulkách, složkách a poznámkách.",
              },
              {
                title: "Finance bez souvislostí nestačí",
                text: "Správa realitního portfolia vyžaduje společný pohled na příjmy, výdaje, cash flow a výkonnost jednotlivých nemovitostí.",
              },
              {
                title: "Termíny se snadno přehlédnou",
                text: "Smlouvy, revize, pojištění a další povinnosti potřebují pevné místo v každodenní správě portfolia.",
              },
            ],
            steps: [
              {
                title: "Nastavíte svá kritéria",
                text: "Lokalita, cena, typ nemovitosti, dispozice a další filtry.",
              },
              {
                title: "SOFTIQ sleduje realitní weby",
                text: "Nové a aktualizované nabídky porovnáváme s vaším nastavením.",
              },
              {
                title: "Dostanete upozornění",
                text: "Podle členství denně, do 2 hodin, nebo okamžitě po nalezení.",
              },
            ],
          }
        : {
            audience: "SOFTIQ for property owners and real estate investors",
            heroLead: "Keep your properties under control.",
            heroAccent: "Portfolio management and finding new opportunities.",
            heroText:
              "Manage your portfolio and property finances in one place. The watchdog also monitors the market for new opportunities.",
            primaryAction: "Get started for free",
            secondaryAction: "Explore features",
            heroStats: [
              ["Portfolio", "all your properties together"],
              ["Finances", "income and expenses at a glance"],
              ["Everything in one place", "for owners and investors"],
            ],
            watchdogEyebrow: "New listings",
            watchdogTitle:
              "When you are ready to grow your portfolio, the watchdog monitors the market for you.",
            watchdogText:
              "Set what you are looking for. SOFTIQ continuously checks new listings and changes across property websites.",
            howEyebrow: "Watchdog",
            howTitle: "One filter, multiple sources, faster decisions.",
            howText:
              "The watchdog complements portfolio management by continuously searching for your next suitable property.",
            stepLabel: "Step",
            painPoints: [
              {
                title: "Good listings disappear within hours",
                text: "Owners and investors who check listings only in the evening often find that the property is already reserved.",
              },
              {
                title: "Manual searching slows you down",
                text: "Every website has different filters and sorting, and updates are easy to miss.",
              },
              {
                title: "Older listings change price too",
                text: "Monitoring updates helps you catch price reductions and new opportunities in your target area.",
              },
            ],
            steps: [
              {
                title: "Set your criteria",
                text: "Location, price, property type, layout, and other filters.",
              },
              {
                title: "SOFTIQ monitors property websites",
                text: "We compare new and updated listings with your settings.",
              },
              {
                title: "Receive an alert",
                text: "Depending on your membership: daily, within two hours, or immediately.",
              },
            ],
          };
    }

    return {
      audience: "Realitní hlídací pes pro investory do nemovitostí",
      heroLead: "Realitní hlídací pes.",
      heroAccent: "Dřív, než nabídka zmizí.",
      heroText:
        "SOFTIQ sleduje více realitních webů za vás. Dostanete upozornění, když se objeví nová nabídka, která odpovídá vašemu hledání.",
      primaryAction: "Zkusit 14 dní zdarma",
      secondaryAction: "Zobrazit ceník",
      heroStats: [
        ["24/7", "monitoring"],
        ["14 dní", "zdarma"],
        ["více webů", "jedno nastavení"],
      ],
      watchdogEyebrow: "Problém trhu",
      watchdogTitle: "Nejlepší příležitost nečeká, až budete mít čas hledat.",
      watchdogText:
        "SOFTIQ není realitní portál. Je to vrstva nad trhem, která hlídá nové nabídky a změny napříč zdroji podle vašich filtrů.",
      howEyebrow: "Jak to funguje",
      howTitle: "Jeden filtr, více zdrojů, rychlejší reakce.",
      howText:
        "Nastavení hlídacího psa je jednoduché: určíte, co hledáte, a služba průběžně kontroluje nové i aktualizované inzeráty.",
      stepLabel: "Krok",
      painPoints: null,
      steps: null,
    };
  }, [language, pageVariant]);

  if (isLoggedIn) {
    return <HomeLandingContent />;
  }

  const defaultPainPoints = [
    {
      title: "Dobré nabídky mizí během hodin",
      text: "Investor, který kontroluje inzerci až večer, často vidí už jen rezervováno.",
    },
    {
      title: "Ruční hledání brzdí",
      text: "Každý web má jiné filtry, jiné řazení a aktualizace se snadno přehlédnou.",
    },
    {
      title: "Cena se mění i u starších inzerátů",
      text: "Hlídání aktualizací pomáhá zachytit slevy a nové šance v lokalitě.",
    },
  ];

  const defaultSteps = [
    {
      title: "Nastavíte investiční kritéria",
      text: "Lokalita, cena, typ nemovitosti, dispozice a další filtry.",
    },
    {
      title: "SOFTIQ sleduje realitní weby",
      text: "Nové a aktualizované nabídky porovnáváme s vaším nastavením.",
    },
    {
      title: "Dostanete upozornění",
      text: "Podle členství denně, do 2 hodin, nebo okamžitě po nalezení.",
    },
  ];

  const monitored = [
    "Byty pod tržní cenou",
    "Rodinné domy k rekonstrukci",
    "Nové nabídky v konkrétní lokalitě",
    "Snížení ceny u sledovaných inzerátů",
    "Investiční příležitosti pro pronájem",
    "Rychlé výpisy pro porovnání trhu",
  ];
  const portfolioSignals = [
    "Přehled všech nemovitostí v portfoliu",
    "Tržní hodnotu a její vývoj",
    "Finance, příjmy, výdaje a cash flow",
    "Dokumenty a smlouvy k nemovitostem",
    "Provozní úkoly a důležité termíny",
    "Výkonnost celého realitního portfolia",
  ];
  const painPoints = copy.painPoints ?? defaultPainPoints;
  const steps = copy.steps ?? defaultSteps;
  const signals =
    pageVariant === "combined" ? portfolioSignals : monitored;

  return (
    <div className="new-page public-editorial min-h-screen bg-softiq-surface text-softiq-copy">
      <main>
        <section className="relative !px-0 !py-0">
          <div
            className={`absolute inset-0 ${
              pageVariant === "combined"
                ? "bg-softiq-primary"
                : "bg-softiq-primary-shade-800 dark:bg-softiq-primary-shade-900"
            }`}
          />
          <div
            className={`relative mx-auto grid min-h-[720px] max-w-[1536px] items-center gap-12 px-6 pb-20 pt-12 sm:pt-14 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:pb-24 lg:pt-16 xl:px-12 ${
              pageVariant === "combined" ? "lg:items-start" : ""
            }`}
          >
            <div
              className={`public-hero-copy max-w-[720px] ${
                pageVariant === "combined" ? "text-white" : "text-white"
              }`}
            >
              <PublicHeroCopy
                eyebrow={copy.audience}
                title={copy.heroLead}
                accent={copy.heroAccent}
                description={copy.heroText}
              />

              <div className="mt-9 flex flex-wrap gap-3">
                <Button
                  asChild
                  variant="brand"
                  size="lg"
                  className="h-12 rounded-full px-6 text-softiq-primary"
                >
                  <Link href={authenticationHref}>
                    {copy.primaryAction}
                    <ArrowRight className="size-4" aria-hidden />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className={`h-12 rounded-full bg-transparent px-6 dark:bg-transparent dark:text-white ${
                    pageVariant === "combined"
                      ? "border-white/25 text-white hover:bg-white/10"
                      : "border-white/25 text-white hover:bg-white/10"
                  }`}
                >
                  <Link
                    href={pageVariant === "combined" ? "#produkty" : "#cenik"}
                  >
                    {copy.secondaryAction}
                  </Link>
                </Button>
              </div>

              <PublicHeroChecklist
                items={
                  language === "cs"
                    ? [
                        "Více realitních webů",
                        "Nové nabídky bez ručního hledání",
                      ]
                    : [
                        "Multiple property websites",
                        "New listings without manual searching",
                      ]
                }
              />
            </div>

            <div className="relative">
              {heroVariant === "watchdog" ? (
                <WatchDogPreview />
              ) : isPendingRegistration ? (
                <Membership />
              ) : (
                <AuthPanel />
              )}
            </div>
          </div>
        </section>

        {pageVariant === "combined" ? <CombinedPortfolioSection /> : null}

        <section
          id="o-psovi"
          className="section-warm bg-softiq-surface-muted px-5 py-20 sm:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              eyebrow={copy.watchdogEyebrow}
              title={copy.watchdogTitle}
              text={copy.watchdogText}
            />

            <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-3">
              {painPoints.map((item) => (
                <article key={item.title} className="relative pl-6">
                  <span
                    className="absolute left-0 top-2 size-2.5 rounded-full bg-softiq-secondary-shade-700 dark:bg-softiq-secondary"
                    aria-hidden
                  />
                  <h3 className="text-lg font-medium leading-7 text-softiq-copy">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-softiq-copy-muted">
                    {item.text}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="jak-to-funguje"
          className="section-light bg-white px-5 py-20 sm:px-8"
        >
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              {pageVariant === "combined" ? (
                <WatchDogPreview />
              ) : (
                <SectionHeading
                  align="left"
                  eyebrow={copy.howEyebrow}
                  title={copy.howTitle}
                  text={copy.howText}
                />
              )}
            </div>

            <div>
              {pageVariant === "combined" ? (
                <SectionHeading
                  align="left"
                  eyebrow={copy.howEyebrow}
                  title={copy.howTitle}
                  text={copy.howText}
                />
              ) : null}

              <div
                className={`border-t border-softiq-border-strong ${
                  pageVariant === "combined" ? "mt-10" : ""
                }`}
              >
                {steps.map((step, index) => (
                  <article
                    key={step.title}
                    className="rounded-2xl border-b border-softiq-border py-6"
                  >
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-softiq-primary/60">
                        {copy.stepLabel} {index + 1}
                      </p>
                      <h3 className="mt-1 text-xl font-medium text-softiq-copy">
                        {step.title}
                      </h3>
                      <p className="mt-2 leading-7 text-softiq-copy-muted">
                        {step.text}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="section-watchdog-signals bg-softiq-primary px-5 py-20 text-white sm:px-8">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-softiq-gold">
                {pageVariant === "combined"
                  ? "Správa realitního portfolia"
                  : "Co můžete hlídat"}
              </p>
              <h2 className="mt-3 text-3xl font-semibold leading-tight sm:text-4xl">
                {pageVariant === "combined"
                  ? "Co máte ve svém portfoliu pod kontrolou."
                  : "Vyberte strategii. SOFTIQ hlídá signály."}
              </h2>
            </div>
            <div className="grid gap-x-10 gap-y-6 sm:grid-cols-2">
              {signals.map((item) => (
                <div key={item}>
                  <span className="leading-6 text-white/86">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {pageVariant !== "combined" ? <WeeklyExpensiveOffers /> : null}

        <DemoSection portfolioFirst={pageVariant === "combined"} />

        <section
          id="cenik"
          className="section-pricing section-warm bg-softiq-surface-muted px-5 py-20 sm:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              eyebrow="Ceník"
              title={
                pageVariant === "combined"
                  ? "Vyberte správu portfolia podle jeho velikosti."
                  : "Začněte zdarma. Zrychlete, až budete chtít náskok."
              }
              text={
                pageVariant === "combined"
                  ? "Začněte se správou realitního portfolia na 14 dní zdarma. Jednotlivé plány se liší rozsahem portfolia a funkcemi hlídacího psa."
                  : "Prvních 14 dní můžete službu vyzkoušet bez závazku. Vyšší plány dávají rychlejší notifikace a více hlídacích psů."
              }
            />

            <p className="mt-6 text-center text-sm font-medium text-softiq-copy-muted">
              Kliknutím na kartu vyberete plán.
            </p>

            <PanelGrid className="mt-8 grid auto-cols-[min(86vw,22rem)] grid-flow-col gap-5 overflow-x-auto overscroll-x-contain px-1 pb-4 pt-4 [scrollbar-width:none] snap-x snap-mandatory lg:mt-12 lg:auto-cols-auto lg:grid-flow-row lg:grid-cols-4 lg:overflow-visible lg:px-0 lg:pb-0">
              {pricingPlans.map((plan) => (
                <div key={plan.value} className="h-full snap-start">
                  <Link
                    href={authenticationHref}
                    onClick={() => {
                      setDoLogin(false);
                      setSelectedProduct(plan.value);
                    }}
                    data-selected={selectedProduct === plan.value}
                    className="block h-full rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-softiq-secondary focus-visible:ring-offset-2"
                  >
                    <article
                      className={`relative flex h-full cursor-pointer flex-col overflow-visible rounded-2xl border p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                        plan.featured
                          ? "pricing-card-featured border-softiq-primary bg-softiq-primary text-white ring-1 ring-softiq-secondary/40 shadow-[0_24px_70px_rgba(27,66,66,0.24)]"
                          : "border-softiq-border-subtle bg-softiq-surface text-softiq-copy"
                      }`}
                    >
                      {plan.featured ? (
                        <p className="absolute -right-2 -top-4 z-10 rotate-[4deg] rounded-full bg-softiq-gold px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-softiq-copy shadow-sm">
                          Pro investory
                        </p>
                      ) : null}
                      <h3 className="text-2xl font-semibold">{plan.name}</h3>
                      <p
                        className={`mt-3 min-h-16 leading-6 ${
                          plan.featured
                            ? "text-white/75"
                            : "text-softiq-copy-muted"
                        }`}
                      >
                        {plan.description}
                      </p>
                      <div className="mt-6">
                        <div className="mb-2 flex min-h-6 items-center gap-2">
                          {plan.priceOriginal > 0 ? (
                            <>
                              <span
                                className={`text-sm line-through decoration-red-400 decoration-2 ${
                                  plan.featured
                                    ? "text-white/55"
                                    : "text-softiq-copy-muted"
                                }`}
                              >
                                {plan.priceOriginal.toLocaleString(
                                  language === "cs" ? "cs-CZ" : "en-US",
                                )}{" "}
                                {language === "cs" ? "Kč" : "CZK"}
                              </span>
                              <span className="rounded-full bg-softiq-secondary-shade-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-softiq-secondary-shade-700 dark:bg-softiq-secondary/15 dark:text-softiq-secondary">
                                {language === "cs"
                                  ? "Akční cena"
                                  : "Special price"}
                              </span>
                            </>
                          ) : null}
                        </div>
                        <span
                          className={`pricing-price text-4xl font-semibold dark:text-softiq-secondary ${
                            plan.featured
                              ? "text-softiq-secondary-shade-200"
                              : "text-softiq-secondary-shade-700"
                          }`}
                        >
                          {plan.price}
                        </span>
                        <span
                          className={`ml-2 text-sm ${
                            plan.featured
                              ? "text-white/65"
                              : "text-softiq-copy-muted"
                          }`}
                        >
                          {plan.priceSub}
                        </span>
                      </div>
                      <div className="mt-6 flex flex-1 flex-col">
                        <p
                          className={`text-[10px] font-bold uppercase tracking-[0.16em] ${
                            plan.featured
                              ? "text-white/55"
                              : "text-softiq-primary/60 dark:text-softiq-secondary"
                          }`}
                        >
                          {language === "cs"
                            ? "Služba Portfolio"
                            : "Portfolio service"}
                        </p>
                        <PricingFeatureList
                          featured={plan.featured}
                          features={plan.portfolioFeatures.map((label) => ({
                            label,
                            included: true,
                          }))}
                        />

                        <div className="mt-6 border-t border-current/15 pt-5">
                          <p
                            className={`text-[10px] font-bold uppercase tracking-[0.16em] ${
                              plan.featured
                                ? "text-white/55"
                                : "text-softiq-primary/60 dark:text-softiq-secondary"
                            }`}
                          >
                            {language === "cs"
                              ? "Služba Hlídací pes"
                              : "Watchdog service"}
                          </p>
                          <PricingFeatureList
                            featured={plan.featured}
                            features={getWatchdogPricingFeatureRows(
                              plan,
                              language,
                            )}
                          />
                        </div>
                      </div>
                    </article>
                  </Link>
                </div>
              ))}
            </PanelGrid>
          </div>
        </section>

        <BlogPreview portfolioFirst={pageVariant === "combined"} />

        <FaqSection />

        <section className="section-light bg-white px-5 py-20 sm:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <Panel variant="shell" className="p-6 sm:p-8">
              <SectionHeading
                align="left"
                eyebrow={
                  pageVariant === "combined"
                    ? "Správa portfolia bez tabulek"
                    : "Rychlost rozhoduje"
                }
                title={
                  pageVariant === "combined"
                    ? "Mějte své realitní portfolio pod kontrolou."
                    : "Nečekejte na další večerní kontrolu inzerce."
                }
                text={
                  pageVariant === "combined"
                    ? "SOFTIQ spojuje přehled nemovitostí, finance, dokumenty a termíny. Každodenní správa realitního portfolia je tak jednodušší a přehlednější."
                    : "Jakmile máte hlídacího psa nastaveného, SOFTIQ dělá nudnou část práce za vás. Vy řešíte jen nabídky, které stojí za reakci."
                }
              />
              <PanelGrid className="mt-8 gap-4 sm:grid-cols-3">
                {(pageVariant === "combined"
                  ? [
                      [Building2, "Přehled portfolia"],
                      [Gauge, "Finance nemovitostí"],
                      [Bell, "Důležité termíny"],
                    ]
                  : [
                      [Gauge, "Rychlé rozhodování"],
                      [Mail, "Emailové reporty"],
                      [Bell, "Okamžité signály"],
                    ]
                ).map(([Icon, label]) => (
                  <Panel
                    variant="compact"
                    key={label as string}
                    className="border-0 bg-softiq-surface-muted p-4"
                  >
                    <Icon className="size-6 text-softiq-primary" aria-hidden />
                    <p className="mt-3 font-semibold text-softiq-copy">
                      {label as string}
                    </p>
                  </Panel>
                ))}
              </PanelGrid>
            </Panel>

            <Panel variant="shell" className="rounded-[28px] p-4">
              <div className="relative overflow-hidden rounded-2xl bg-softiq-surface-sunken">
                <Image
                  src="/image/cta/web-banner.png"
                  alt="Realitní dashboard SOFTIQ"
                  width={1120}
                  height={720}
                  className="h-auto w-full object-cover"
                />
              </div>
              <PanelGrid className="mt-4 sm:grid-cols-3">
                {(pageVariant === "combined"
                  ? [
                      ["Hodnota portfolia", "12 450 000 Kč", "+4,8 % za rok"],
                      ["Cash flow", "32 800 Kč", "tento měsíc"],
                      ["Dokumenty", "Vše platné", "další termín za 42 dní"],
                    ]
                  : [
                      ["Praha 7", "Prodej bytu 2+kk, 65 m2", "5 689 000 Kč"],
                      ["Brno", "Byt 3+kk s předzahrádkou", "5 249 000 Kč"],
                      ["Olomouc", "Byt k rekonstrukci", "2 500 000 Kč"],
                    ]
                ).map(([city, deal, time]) => (
                  <Panel
                    variant="compact"
                    key={`${city}-${deal}`}
                    className="p-3"
                  >
                    <p className="flex items-center gap-1 text-xs font-semibold text-softiq-primary">
                      {pageVariant === "combined" ? (
                        <Building2 className="size-3" aria-hidden />
                      ) : (
                        <MapPin className="size-3" aria-hidden />
                      )}
                      {city}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-softiq-copy">
                      {deal}
                    </p>
                    <p className="mt-1 text-xs text-softiq-copy-muted">
                      {time}
                    </p>
                  </Panel>
                ))}
              </PanelGrid>
            </Panel>
          </div>
        </section>

        <ReviewsPanel portfolioFirst={pageVariant === "combined"} />
      </main>
    </div>
  );
}
